/**
 * The parent portal's four type steps.
 *
 * The dashboard used to have two: `text-base sm:text-lg font-medium` for anything that
 * mattered and `text-xs text-gray-400` for anything that did not. A child's name and the name
 * of a class they are enrolled in were set identically, so the page had no way to say which
 * was the subject and which was the detail — and the fix kept being another bordered box.
 *
 * Four steps carry the whole hierarchy on a flat surface, with no container nesting at all:
 *
 * - `SUBJECT` — whose row this is. One per group; there is never a second on the same level.
 * - `OBJECT`  — the thing that belongs to them: a class, an invoice, a session.
 * - `ATTRIBUTE` — what is true about that object. Grey, and never given a pill; a pill is a
 *   state affordance, and spending it on a date range is what made the old rows read as four
 *   separate objects.
 * - `SECTION` — the uppercase eyebrow naming the block. Small enough not to compete with the
 *   subject beneath it, spaced enough to still read as a heading.
 *
 * Exported as class strings rather than Tailwind theme keys so the scale can be grepped, and
 * so a step can be composed with layout classes at the call site without a plugin.
 */

/** Step 1 — whose row this is. */
export const TYPE_SUBJECT = 'text-[19px] font-bold tracking-tight leading-tight text-gray-900';

/** Step 2 — the class, invoice or session belonging to the subject. */
export const TYPE_OBJECT = 'text-sm font-semibold leading-snug text-gray-900';

/** Step 3 — what is true about it. Never inside a pill. */
export const TYPE_ATTRIBUTE = 'text-xs font-normal leading-snug text-gray-500';

/** Step 4 — the block heading. */
export const TYPE_SECTION = 'text-[10px] font-semibold uppercase tracking-[0.13em] text-gray-400';

/**
 * Clock and date text.
 *
 * `tabular-nums` is the whole point: without it a column of times jitters as the glyph widths
 * change, which on a list of sessions reads as sloppiness rather than as a column.
 */
export const TYPE_CLOCK = 'text-xs tabular-nums text-gray-600';

/**
 * The same four steps, one notch larger from `lg` up.
 *
 * The scale above was measured on a 390px phone, and the dashboard then shipped a two-column
 * desktop layout that gave the left column ~900px and kept rendering phone type into it — a
 * 19px child's name and an 11px class name floating in a card three times wider than the one
 * they were drawn for. The card read as small because its contents were.
 *
 * The fix is a proportional step, not a new step: every level moves together so the *ratios*
 * that carry the hierarchy are unchanged, and `SECTION` deliberately does not move at all —
 * the eyebrow is quiet on purpose, and growing it would put it in competition with the
 * subject beneath it.
 *
 * Opt-in per call site rather than folded into the constants above, because "wide" describes
 * the container, not the viewport: the Students page renders `TYPE_OBJECT` inside a
 * three-across card grid that is still narrow at 1440px, and would be actively worse with
 * bigger type in it.
 *
 * `SECTION` is the exception to the exception. It was held at 10px on the reasoning that an
 * eyebrow should not compete with the subject beneath it — true at 19px, and no longer true
 * once the subject is 22px and the card is 900px wide, where a 10px heading stopped reading
 * as the name of the block and started reading as a caption on it. It moves too, by less: the
 * gap between it and `SUBJECT` still widens.
 *
 * It is also the one step whose unit is the *page* rather than the card: the four blocks of
 * the dashboard are peers, and two different eyebrow sizes across them would read as a
 * mistake rather than as a hierarchy — so the narrower right-hand column takes it too.
 */

/** Step 1, in a full-width column. */
export const TYPE_SUBJECT_WIDE = `${TYPE_SUBJECT} lg:text-[22px]`;

/** Step 2, in a full-width column. */
export const TYPE_OBJECT_WIDE = `${TYPE_OBJECT} lg:text-base`;

/** Step 3, in a full-width column. */
export const TYPE_ATTRIBUTE_WIDE = `${TYPE_ATTRIBUTE} lg:text-[13px]`;

/** Clock text, in a full-width column. */
export const TYPE_CLOCK_WIDE = `${TYPE_CLOCK} lg:text-[13px]`;

/** Step 4, in a full-width column. Tracking eases as the size grows, as tracking should. */
export const TYPE_SECTION_WIDE = `${TYPE_SECTION} lg:text-[13px] lg:tracking-[0.11em]`;
